import React, { useState } from 'react';
import { 
  PawPrint, 
  Heart, 
  Calendar, 
  DollarSign, 
  FileText, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  MapPin,
  Phone,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface Pet {
  id: string;
  name: string;
  type: string;
  breed: string;
  age: number;
  weight: string;
  microchip: string;
  photo?: string;
}

interface PetHealth {
  id: string;
  petId: string;
  type: string;
  date: string;
  vet: string;
  cost: number;
  notes: string;
  nextDue?: string;
}

interface PetExpense {
  id: string;
  petId: string;
  category: string;
  amount: number;
  date: string;
  description: string;
}

export function MobilePetsDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'pets' | 'health' | 'expenses' | 'care'>('pets');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data
  const pets: Pet[] = [
    {
      id: '1',
      name: 'Buddy',
      type: 'Dog',
      breed: 'Golden Retriever',
      age: 3,
      weight: '65 lbs',
      microchip: '123456789'
    },
    {
      id: '2',
      name: 'Whiskers',
      type: 'Cat',
      breed: 'Domestic Shorthair',
      age: 5,
      weight: '12 lbs',
      microchip: '987654321'
    },
    {
      id: '3',
      name: 'Fluffy',
      type: 'Rabbit',
      breed: 'Holland Lop',
      age: 2,
      weight: '4 lbs',
      microchip: '456789123'
    }
  ];

  const healthRecords: PetHealth[] = [
    {
      id: '1',
      petId: '1',
      type: 'Vaccination',
      date: '2024-01-15',
      vet: 'Dr. Smith',
      cost: 85,
      notes: 'Annual vaccinations - DHPP, Rabies',
      nextDue: '2025-01-15'
    },
    {
      id: '2',
      petId: '1',
      type: 'Checkup',
      date: '2024-01-10',
      vet: 'Dr. Smith',
      cost: 65,
      notes: 'Annual wellness exam - healthy'
    },
    {
      id: '3',
      petId: '2',
      type: 'Dental Cleaning',
      date: '2024-01-20',
      vet: 'Dr. Johnson',
      cost: 150,
      notes: 'Dental cleaning and extraction'
    }
  ];

  const expenses: PetExpense[] = [
    {
      id: '1',
      petId: '1',
      category: 'Food',
      amount: 45.99,
      date: '2024-01-25',
      description: 'Premium dog food'
    },
    {
      id: '2',
      petId: '1',
      category: 'Toys',
      amount: 12.99,
      date: '2024-01-20',
      description: 'Chew toys and balls'
    },
    {
      id: '3',
      petId: '2',
      category: 'Litter',
      amount: 18.99,
      date: '2024-01-22',
      description: 'Premium cat litter'
    }
  ];

  const careSchedule = [
    {
      id: '1',
      petId: '1',
      task: 'Feed Breakfast',
      time: '7:00 AM',
      frequency: 'Daily',
      status: 'completed'
    },
    {
      id: '2',
      petId: '1',
      task: 'Walk',
      time: '6:00 PM',
      frequency: 'Daily',
      status: 'pending'
    },
    {
      id: '3',
      petId: '2',
      task: 'Feed Dinner',
      time: '6:00 PM',
      frequency: 'Daily',
      status: 'pending'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const renderPets = () => (
    <div className="space-y-3">
      {pets.map((pet) => (
        <Card key={pet.id} className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <PawPrint size={24} className="text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">{pet.name}</h3>
              <p className="text-sm text-gray-600">{pet.breed} • {pet.age} years old</p>
              <p className="text-xs text-gray-500">{pet.weight} • Microchip: {pet.microchip}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost">
                <Eye size={14} />
              </Button>
              <Button size="sm" variant="ghost">
                <Edit size={14} />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderHealth = () => (
    <div className="space-y-3">
      {healthRecords.map((record) => (
        <Card key={record.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{record.type}</h3>
                <p className="text-sm text-gray-600">{record.vet}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">${record.cost}</p>
                <p className="text-xs text-gray-500">{record.date}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">{record.notes}</p>
            {record.nextDue && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <Calendar size={14} />
                <span>Next due: {record.nextDue}</span>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  const renderExpenses = () => (
    <div className="space-y-3">
      {expenses.map((expense) => (
        <Card key={expense.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={16} className="text-green-500" />
                <h3 className="font-medium text-gray-900">{expense.category}</h3>
              </div>
              <p className="text-sm text-gray-600">{expense.description}</p>
              <p className="text-xs text-gray-500">{expense.date}</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-900">${expense.amount}</p>
              <Button size="sm" variant="ghost">
                <Edit size={14} />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderCare = () => (
    <div className="space-y-3">
      {careSchedule.map((task) => (
        <Card key={task.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Heart size={16} className="text-red-500" />
                <h3 className="font-medium text-gray-900">{task.task}</h3>
              </div>
              <p className="text-sm text-gray-600">{task.time} • {task.frequency}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(task.status)}`}>
                {task.status}
              </span>
              <Button size="sm" variant="ghost">
                <Edit size={14} />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Pets Management</h2>
          <p className="text-sm text-gray-600">Manage pet care, health, and expenses</p>
        </div>
        <Button size="sm">
          <Plus size={16} className="mr-2" />
          Add Pet
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search pets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'pets' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('pets')}
        >
          <PawPrint size={16} className="mr-2" />
          Pets
        </Button>
        <Button
          variant={activeTab === 'health' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('health')}
        >
          <Heart size={16} className="mr-2" />
          Health
        </Button>
        <Button
          variant={activeTab === 'expenses' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('expenses')}
        >
          <DollarSign size={16} className="mr-2" />
          Expenses
        </Button>
        <Button
          variant={activeTab === 'care' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('care')}
        >
          <Calendar size={16} className="mr-2" />
          Care
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'pets' && renderPets()}
        {activeTab === 'health' && renderHealth()}
        {activeTab === 'expenses' && renderExpenses()}
        {activeTab === 'care' && renderCare()}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full">
          <Calendar size={16} className="mr-2" />
          Schedule Vet Visit
        </Button>
        <Button variant="outline" className="w-full">
          <DollarSign size={16} className="mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Alerts */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={16} className="text-yellow-600" />
          <h4 className="font-medium text-yellow-800">Upcoming Reminders</h4>
        </div>
        <div className="space-y-1 text-sm text-yellow-700">
          <p>• Buddy's vaccination due in 2 weeks</p>
          <p>• Whiskers' dental cleaning in 1 month</p>
        </div>
      </div>
    </div>
  );
} 